import React, { useState, useContext, useEffect } from 'react';
import { Box, Dialog, Divider, Stack, Typography } from '@mui/material';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Icon, divIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { getToken } from '../../../api/Account';
import NoData from '../../../components/NoData';
import { EstadoError } from '../../../components/base';
import { simpleMessage } from '../../../helpers/Helpers';
import { formatearFecha, formatearFechaHora } from '../../../helpers/formato';
import { EncabezadoReporte } from '../../../components/reportes/EncabezadoReporte';
import { getUserTrackingAsync } from '../../../api/Report';
import { getGestoresAsync } from '../../../api/Users';
import { DataContext } from '../../../context/DataContext';
import { light } from '../../../theme/tokens';
import {
	esIncidencia,
	limitesDelMapa,
	resumenRecorrido,
	tieneUbicacion,
	trazosPorGestor
} from './rastreo';

const INCIDENCIAS = {
	4: 'Ubicación desactivada en el teléfono',
	5: 'Se retiró el permiso de ubicación',
	6: 'Sin permiso de ubicación concedido'
};

// Si no hay ningún punto que ubicar, el mapa abre aquí.
const CENTRO_INICIAL = [12.629763888889, -87.127811111111];

// El mapa siempre es claro (las teselas de OpenStreetMap no tienen modo oscuro): la línea y
// la alerta usan los colores del tema claro.
const COLOR_TRAZO = light.primary;

// Triangulo de advertencia dibujado en el navegador, para que un corte no se confunda
// con un gestor en ruta. No se trae de un CDN: sin internet el mapa quedaria sin iconos.
const iconoAlerta = divIcon({
	className: '',
	html: `<div style="width:0;height:0;border-left:12px solid transparent;border-right:12px solid transparent;border-bottom:21px solid ${light.error};filter:drop-shadow(0 0 2px rgba(0,0,0,.6))"></div>`,
	iconSize: [24, 21],
	iconAnchor: [12, 21],
	popupAnchor: [0, -22]
});

const iconoMoto = new Icon({
	iconUrl: require('../../../components/media/motoIconAzul.png'),
	iconSize: [50, 50]
});

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = 'Sesión activa en otro dispositivo';

const hora = (fecha) => moment(fecha).format('hh:mm A');

/**
 * Recorrido de un gestor en un día, o la última posición conocida de cada gestor ("Todos":
 * el servidor no filtra por fecha en ese caso).
 */
const ReporteUserTracking = () => {
	const [data, setData] = useState([]);
	// null mientras carga; "ok", "error" o "conexion" al terminar.
	const [estado, setEstado] = useState(null);
	const [gestorName, setGestorName] = useState('');
	const { selectedGestor, selectedFecha } = useParams();
	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	const token = getToken();
	const todos = selectedGestor === 't';

	// Cada llamada devuelve { result } o { error }; sin red lanza en vez de devolver statusResponse false.
	const pedir = async (llamada) => {
		try {
			const r = await llamada();
			return r.statusResponse ? { result: r.data.result } : { error: r.error || 'error' };
		} catch (e) {
			return { error: 'conexion' };
		}
	};

	const cargar = async () => {
		setEstado(null);
		setIsLoading(true);
		// La lista de gestores solo da el nombre: si falla, el reporte sigue.
		const [gestores, puntos] = await Promise.all([
			todos ? Promise.resolve({ result: [] }) : pedir(() => getGestoresAsync(token)),
			pedir(() => getUserTrackingAsync(token, todos ? 0 : selectedGestor, selectedFecha))
		]);
		setIsLoading(false);
		if (puntos.error === 'Unauthorized' || gestores.error === 'Unauthorized') {
			setIsUnautorized(true);
			return;
		}
		if (puntos.error) {
			if (puntos.error === ERROR_OTRA_SESION) simpleMessage(puntos.error, 'error');
			setEstado(puntos.error === 'conexion' ? 'conexion' : 'error');
			return;
		}
		const lista = puntos.result || [];
		if (todos) {
			setGestorName('Todos');
		} else {
			const gestor = (gestores.result || []).find((item) => item.id.toString() === selectedGestor);
			// Un gestor dado de baja ya no está en la lista: su nombre viene en cada punto.
			setGestorName(gestor?.fullName ?? lista[0]?.fullName ?? `Gestor #${selectedGestor}`);
		}
		setData(lista);
		setEstado('ok');
	};

	useEffect(() => {
		cargar();
	}, []);

	const parametros = todos
		? [
				{ label: 'Gestor Seleccionado', valor: 'Todos' },
				{ label: 'Muestra', valor: 'La última posición conocida de cada gestor' }
		  ]
		: [
				{ label: 'Gestor Seleccionado', valor: gestorName },
				{ label: 'Fecha', valor: formatearFecha(selectedFecha) }
		  ];

	const ubicables = data.filter(tieneUbicacion);
	const trazos = trazosPorGestor(data);
	const limites = limitesDelMapa(data);
	const resumen = resumenRecorrido(data);

	let contenido = null;
	if (estado === 'error' || estado === 'conexion') {
		// No se dice "no envió su ubicación": no se sabe, la consulta falló.
		contenido = (
			<EstadoError
				titulo={
					estado === 'conexion'
						? 'No se pudo conectar con el servidor'
						: 'No se pudo cargar el recorrido'
				}
				onReintentar={cargar}
			/>
		);
	} else if (estado === 'ok' && data.length === 0) {
		contenido = (
			<NoData
				titulo="Sin recorrido"
				descripcion={
					todos
						? 'Ningún gestor ha enviado su ubicación todavía.'
						: `${gestorName} no envió su ubicación el ${formatearFecha(selectedFecha)}.`
				}
			/>
		);
	} else if (estado === 'ok') {
		// Las incidencias sin coordenadas cuentan como cortes, pero no tienen triángulo en el mapa.
		const cortesSinLugar = data.filter((p) => esIncidencia(p.kind) && !tieneUbicacion(p)).length;
		const cortesEnMapa = resumen.incidencias - cortesSinLugar;
		const puntosSinLugar = data.filter((p) => !esIncidencia(p.kind) && !tieneUbicacion(p)).length;
		contenido = (
			<>
				<Stack
					direction="row"
					flexWrap="wrap"
					justifyContent="center"
					columnGap={3}
					rowGap={0.5}
					sx={{ mb: 1.5, fontVariantNumeric: 'tabular-nums' }}
				>
					{todos ? (
						<Typography variant="body2">
							{`Última posición de ${data.length} ${data.length === 1 ? 'gestor' : 'gestores'}`}
						</Typography>
					) : (
						<>
							<Typography variant="body2">{`${resumen.puntos} puntos de recorrido`}</Typography>
							<Typography variant="body2">{`Primera señal ${hora(resumen.primera)}`}</Typography>
							<Typography variant="body2">{`Última señal ${hora(resumen.ultima)}`}</Typography>
						</>
					)}
					{resumen.incidencias > 0 ? (
						<Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
							{`${resumen.incidencias} ${resumen.incidencias === 1 ? 'corte' : 'cortes'} de ubicación`}
							{cortesEnMapa > 0 ? ' (triángulo rojo)' : ''}
							{cortesSinLugar > 0 ? `, ${cortesSinLugar} sin lugar en el mapa` : ''}
						</Typography>
					) : null}
					{puntosSinLugar > 0 ? (
						<Typography variant="body2" sx={{ color: 'text.secondary' }}>
							{`${puntosSinLugar} puntos sin coordenadas: no aparecen en el mapa`}
						</Typography>
					) : null}
				</Stack>

				<Box
					component={MapContainer}
					// react-leaflet ignora `bounds` si recibe `center` y `zoom`: van solo sin puntos.
					{...(limites
						? { bounds: limites, boundsOptions: { padding: [40, 40], maxZoom: 17 } }
						: { center: CENTRO_INICIAL, zoom: 16 })}
					sx={{ flex: 1, minHeight: 360, borderRadius: 2.5, border: 1, borderColor: 'divider' }}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{/* El trayecto va como una linea y no como marcadores: una jornada deja
					    cientos de puntos, y esa misma cantidad de marcadores vuelve el mapa
					    inservible. Los marcadores quedan para lo que importa senalar. */}
					{trazos.map((trazo) => (
						<Polyline key={trazo.nombre} positions={trazo.puntos} color={COLOR_TRAZO} weight={4} />
					))}

					{/* Una incidencia sin coordenadas no se puede ubicar; se omite del mapa. */}
					{ubicables.map((item) => {
						const corte = esIncidencia(item.kind);
						return (
							<Marker
								key={item.id}
								position={[item.latitude, item.longitude]}
								icon={corte ? iconoAlerta : iconoMoto}
							>
								<Popup>
									{/* El globo de Leaflet es blanco también en modo oscuro: letra del tema claro. */}
									<Stack sx={{ textAlign: 'center', color: light.text }}>
										<Typography component="h2" sx={{ fontWeight: 700, fontSize: 14 }}>
											{item.fullName}
										</Typography>
										<Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
											{/* Con "Todos" la posición puede ser de otro día. */}
											{todos ? formatearFechaHora(item.fecha) : hora(item.fecha)}
										</Typography>
										{corte ? (
											<Typography component="span" sx={{ color: light.error, fontWeight: 700, fontSize: 13 }}>
												{INCIDENCIAS[item.kind]}
											</Typography>
										) : (
											<Typography component="span" sx={{ fontSize: 13 }}>
												{`Precisión: ${Number(item.accuracy ?? 0).toFixed(2)} metros`}
											</Typography>
										)}
									</Stack>
								</Popup>
							</Marker>
						);
					})}
				</Box>
			</>
		);
	}

	return (
		<Dialog fullScreen open={true} PaperProps={{ 'aria-label': 'Reporte de Rastreo de Usuarios' }}>
			<EncabezadoReporte titulo="Reporte de Rastreo de Usuarios" parametros={parametros} sinImprimir />
			<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: { xs: 2, sm: 3 }, pb: 2 }}>
				<Divider sx={{ my: 1.5 }} />
				{contenido}
			</Box>
		</Dialog>
	);
};
export default ReporteUserTracking;
